import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

// ---- Session = httpOnly SameSite=Lax cookies only. Mutations carry the double-submit CSRF token read from the csrf_token cookie.
const CSRF_COOKIE = "csrf_token";
export const readCsrf = () => (document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`)) || [])[1] || "";
api.interceptors.request.use((cfg) => {
  const csrf = readCsrf();
  if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  return cfg;
});

// ---- Global session handling: expired access token -> single-flight refresh -> retry once -> else session-expired flow.
export const SESSION_EXPIRED_MSG = "Your session expired. Please sign in again.";
export const SESSION_EXPIRED_EVENT = "neec:session-expired";
const AUTH_SKIP = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/forgot-password", "/auth/reset-password", "/public/"];
const isAuthFailure = (e) => e?.response?.status === 401 && !AUTH_SKIP.some((p) => (e.config?.url || "").includes(p));
let refreshing = null;
const refreshSession = () => {
  if (!refreshing) {
    refreshing = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, headers: { "X-CSRF-Token": readCsrf() } })
      .then((r) => r.data.csrf_token)
      .finally(() => { refreshing = null; });
  }
  return refreshing;
};
const expireSession = (e) => {
  e.sessionExpired = true;
  if (e.response) e.response.data = { detail: { code: "session_expired", message: SESSION_EXPIRED_MSG } };
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  return Promise.reject(e);
};
api.interceptors.response.use((r) => r, async (e) => {
  if (!isAuthFailure(e)) return Promise.reject(e);
  const cfg = e.config || {};
  if (cfg._retried) return expireSession(e);
  cfg._retried = true; // mutating requests are retried at most once, only after a successful refresh
  try {
    const csrf = await refreshSession();
    cfg.headers = { ...(cfg.headers || {}), "X-CSRF-Token": csrf };
    return api.request(cfg);
  } catch {
    return expireSession(e);
  }
});

export const fileUrl = (id) => (id ? `${API}/files/${id}` : "");
export const publicFileUrl = (token, id) => (token && id ? `${API}/public/files/${token}/${id}` : "");
// Tenant operating currency (set once from /settings). Legacy tenants have no currency -> USD, rendering exactly as before.
let _tenantCurrency = "USD";
export const setTenantCurrency = (cur) => { _tenantCurrency = cur || "USD"; };
const SYMBOL = { USD: "$", CAD: "CA$", JMD: "J$" };
export const fmtMoney = (v, cur) => `${SYMBOL[cur || _tenantCurrency] || `${cur || _tenantCurrency} `}${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtUSD = (v) => fmtMoney(v, "USD");
export const docMoney = (doc) => (v) => fmtMoney(v, doc?.currency);
export const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s.length === 10 ? `${s}T12:00:00` : s);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
export const today = () => new Date().toISOString().slice(0, 10);
export const errMsg = (e) => {
  if (e?.sessionExpired) return SESSION_EXPIRED_MSG;
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return ["Token expired", "Invalid token", "Invalid token type", "Not authenticated", "Signature has expired"].includes(d) || d.startsWith("Session expired") ? SESSION_EXPIRED_MSG : d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d.message || d.msg || String(d);
};

export const METHODS = [
  ["cash", "Cash"], ["card", "External Card Payment (manually recorded)"], ["venmo", "Venmo"], ["zelle", "Zelle"],
  ["cashapp", "Cash App"], ["check", "Check"], ["ach", "Bank Transfer (ACH)"], ["other", "Other"],
];
export const methodLabel = (m) => (METHODS.find((x) => x[0] === m) || [m, m])[1];
export const EXPENSE_PAY_METHODS = ["Business Debit", "Business Card", "Personal Card", "Cash", "Check", "Other"];

export const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600", sent: "bg-blue-50 text-blue-700", viewed: "bg-indigo-50 text-indigo-700",
  accepted: "bg-emerald-50 text-emerald-700", declined: "bg-red-50 text-red-700", scheduled: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700", completed: "bg-emerald-50 text-emerald-700", partially_paid: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700", overdue: "bg-red-50 text-red-700", received: "bg-emerald-50 text-emerald-700",
};
export const statusLabel = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const openBlob = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
